// useBalanceConstants.tsx
import { GithubFormat } from './formatCode';
import { Constant } from '../components/GameSettingsPanel';
import { formatConstantNameToLabel, formatLabelToConstantName } from './labelHelpers';

const balanceFileCandidates = ['BalanceSettings.ts', 'BalanceConstants.ts'];

export const extractBalanceConstants = (code: GithubFormat): Constant[] => {
    // console.log('All file paths:');
    // code.forEach(file => console.log(file.path));

    // Find the BalanceSettings.ts (preferred) or BalanceConstants.ts file
    const constantsFile = code.find(file =>
        balanceFileCandidates.some(name => file.path.toLowerCase().includes(name.toLowerCase()))
    );
    // console.log('constantsFile: ', constantsFile);

    if (!constantsFile) {
        // console.warn('BalanceConstants.ts not found in code');
        return [];
    }

    let constants: Constant[] = [];

    // --- Attempt to extract constants from the old direct export format (numbers and booleans) ---
    const directNumberRegex = /export const (\w+)\s*=\s*(-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?);/g;
    let match;
    while ((match = directNumberRegex.exec(constantsFile.content)) !== null) {
        const label = formatConstantNameToLabel(match[1]);
        constants.push({ label, value: match[2].trim(), type: 'number' });
    }

    const directBooleanRegex = /export const (\w+)\s*=\s*(true|false);/gi;
    let matchBool;
    while ((matchBool = directBooleanRegex.exec(constantsFile.content)) !== null) {
        const label = formatConstantNameToLabel(matchBool[1]);
        constants.push({ label, value: matchBool[2].toLowerCase(), type: 'boolean' });
    }

    // --- Extract constants explicitly from the BALANCE_SETTINGS or BALANCE_CONSTANTS object ---
    const balanceObjectRegex = /export const (BALANCE_SETTINGS|BALANCE_CONSTANTS)\s*=\s*({[\s\S]*?})/;
    const balanceConstantsMatch = balanceObjectRegex.exec(constantsFile.content);

    if (balanceConstantsMatch) {
        const constantsContent = balanceConstantsMatch[2];
        // Numbers in object
        const constantNumberPairRegex = /(\w+):\s*(-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?)/g;
        let pairNumMatch;
        while ((pairNumMatch = constantNumberPairRegex.exec(constantsContent)) !== null) {
            const label = formatConstantNameToLabel(pairNumMatch[1]);
            constants.push({ label, value: pairNumMatch[2].trim(), type: 'number' });
        }
        // Booleans in object
        const constantBooleanPairRegex = /(\w+):\s*(true|false)/gi;
        let pairBoolMatch;
        while ((pairBoolMatch = constantBooleanPairRegex.exec(constantsContent)) !== null) {
            const label = formatConstantNameToLabel(pairBoolMatch[1]);
            constants.push({ label, value: pairBoolMatch[2].toLowerCase(), type: 'boolean' });
        }
    }

    // --- Extract arrays of numbers/booleans ---
    // Direct arrays: export const SOME_ARRAY = [ ... ];
    const directArrayRegex = /export const (\w+)\s*=\s*\[([\s\S]*?)\];/g;
    let directArrayMatch;
    while ((directArrayMatch = directArrayRegex.exec(constantsFile.content)) !== null) {
        const arrayName = directArrayMatch[1];
        const arrayBody = directArrayMatch[2];
        const tokens = tokenizeArrayItems(arrayBody);
        if (tokens.length === 0) continue;
        const withoutComments = tokens.map(t => stripInlineCommentsOutsideQuotes(t.trim()));
        const unquoted = withoutComments.map(t => stripQuotes(t.trim()));
        const allNumbers = unquoted.every(v => isNumericLiteral(v));
        const allBooleans = unquoted.every(v => isBooleanLiteral(v));
        if (!allNumbers && !allBooleans) continue;
        const labelBase = formatConstantNameToLabel(arrayName);
        for (let i = 0; i < unquoted.length; i++) {
            const label = `${labelBase} ${i + 1}`;
            if (allNumbers) {
                constants.push({ label, value: unquoted[i], type: 'number', groupKey: arrayName, groupIndex: i });
            } else {
                constants.push({ label, value: unquoted[i].toLowerCase(), type: 'boolean', groupKey: arrayName, groupIndex: i });
            }
        }
    }

    // Object arrays: inside BALANCE_SETTINGS / BALANCE_CONSTANTS
    if (balanceConstantsMatch) {
        const constantsContent = balanceConstantsMatch[2];
        const keyArrayPairRegex = /(\w+):\s*\[([\s\S]*?)\]/g;
        let arrMatch;
        while ((arrMatch = keyArrayPairRegex.exec(constantsContent)) !== null) {
            const keyName = arrMatch[1];
            const arrayBody = arrMatch[2];
            const tokens = tokenizeArrayItems(arrayBody);
            if (tokens.length === 0) continue;
            const withoutComments = tokens.map(t => stripInlineCommentsOutsideQuotes(t.trim()));
            const unquoted = withoutComments.map(t => stripQuotes(t.trim()));
            const allNumbers = unquoted.every(v => isNumericLiteral(v));
            const allBooleans = unquoted.every(v => isBooleanLiteral(v));
            if (!allNumbers && !allBooleans) continue;
            const labelBase = formatConstantNameToLabel(keyName);
            for (let i = 0; i < unquoted.length; i++) {
                const label = `${labelBase} ${i + 1}`;
                if (allNumbers) {
                    constants.push({ label, value: unquoted[i], type: 'number', groupKey: keyName, groupIndex: i });
                } else {
                    constants.push({ label, value: unquoted[i].toLowerCase(), type: 'boolean', groupKey: keyName, groupIndex: i });
                }
            }
        }
    }
    
    // console.log('Extracted constants:', constants);
    return constants;
};

// Convert Title Case Spaces back to UPPER_CASE_WITH_UNDERSCORES
const getLabelToConstantName = (label: string): string => {
    return formatLabelToConstantName(label);
};

// Apply the updated constants back to the code
export const applyBalanceConstants = (code: GithubFormat, constants: Constant[]): GithubFormat => {
    // Find the BalanceSettings.ts (preferred) or BalanceConstants.ts file
    let constantsFileIndex = code.findIndex(file =>
        balanceFileCandidates.some(name => file.path.includes(name))
    );

    // If none found, create a new BalanceSettings.ts file with BALANCE_SETTINGS
    if (constantsFileIndex === -1) {
        // Build BALANCE_SETTINGS object from provided constants
        const lines = constants.map(c => `${formatLabelToConstantName(c.label)}: ${c.value}`).join(',\n');
        const newContent = `// Do not rename or remove this file or comment.\n// Game balance settings go here, so the user can easily tweak the variables within the game settings panel.\n// Avoid using nested objects or arrays; all settings must be at the same level within the BALANCE_SETTINGS object. Prefer SNAKE_CASE for names.\nexport const BALANCE_SETTINGS = {\n${lines}\n};`;
        const newFilePath = 'src/config/BalanceSettings.ts';
        const newFile = { path: newFilePath, content: newContent } as any;
        const updatedCodeOnCreate = [...code, newFile];
        // Return early since content already includes applied values
        return updatedCodeOnCreate as GithubFormat;
    }
    
    // Create a copy of the code array
    const updatedCode = [...code];
    const originalContent = updatedCode[constantsFileIndex].content;
    let updatedContent = originalContent;
    
    // Extract all existing constants from the file to get their official names
    const constantMap = new Map();

    // --- Populate map from old direct export format ---
    // const directConstantRegex = /export const (\w+)\s*=\s*([^;{]+?);/g;
    // let directMatch;
    // while ((directMatch = directConstantRegex.exec(originalContent)) !== null) {
    //     const constName = directMatch[1];
    //     const constValue = directMatch[2].trim();
    //     const label = formatConstantNameToLabel(constName);
    //     constantMap.set(label, { name: constName, value: constValue, type: 'direct' });
    // }

    // --- Populate map from direct export format (legacy) ---
    const directConstantRegexMap = /export const (\w+)\s*=\s*([^;{]+?);/g;
    let directMatch;
    while ((directMatch = directConstantRegexMap.exec(originalContent)) !== null) {
        const constName = directMatch[1];
        const constValue = directMatch[2].trim();
        const label = formatConstantNameToLabel(constName);
        if (!constantMap.has(label)) {
            constantMap.set(label, { name: constName, value: constValue, type: 'direct' });
        }
    }

    // --- Populate map from BALANCE_SETTINGS then BALANCE_CONSTANTS (SETTINGS takes priority) ---
    const objectVariants = ['BALANCE_SETTINGS', 'BALANCE_CONSTANTS'];
    for (const parentName of objectVariants) {
        const exportedObjectConstantRegex = new RegExp(`export const ${parentName}\\s*=\\s*({[^}]*});?`, 'gs');
        let objectMatchResult;
        while ((objectMatchResult = exportedObjectConstantRegex.exec(originalContent)) !== null) {
            const constantsContent = objectMatchResult[1];
            const constantPairRegex = /(\w+):\s*([^,\n}]+),?/g;
            let pairMatch;
            while ((pairMatch = constantPairRegex.exec(constantsContent)) !== null) {
                const constName = pairMatch[1];
                const constValue = pairMatch[2].trim();
                const label = formatConstantNameToLabel(constName);
                if (!constantMap.has(label)) {
                    constantMap.set(label, { name: constName, value: constValue, type: 'object', parentObject: parentName });
                }
            }
        }
    }
    
    // Update each constant in the file content
    constants.forEach(constant => {
        if (constantMap.has(constant.label)) {
            const originalConst = constantMap.get(constant.label);
            const originalNormalized = stripQuotes(stripInlineCommentsOutsideQuotes(originalConst.value));
            if (originalNormalized !== constant.value) {
                if (originalConst.type === 'direct') {
                    const constRegex = new RegExp(`(export const ${originalConst.name}\\s*=\\s*)([^;]+?)(\\s*(?:\\/\\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/))?(;)`, 'g');
                    updatedContent = updatedContent.replace(constRegex, `$1${constant.value}$3$4`);
                } else if (originalConst.type === 'object') {
                    // Preserve any trailing inline comments
                    const parentObjectRegex = new RegExp(`(export const ${originalConst.parentObject}\\s*=\\s*{[^}]*?)(${originalConst.name}:\\s*)([^,\\n}]+?)(\\s*(?:\\/\\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/))?(?=,|\\s*})`, 's');
                    updatedContent = updatedContent.replace(parentObjectRegex, `$1$2${constant.value}$4`);
                }
            }
        }
    });

    // --- Handle grouped numeric arrays ---
    const groupedByKey = new Map<string, Constant[]>();
    for (const c of constants) {
        if (typeof c.groupKey === 'string' && typeof c.groupIndex === 'number') {
            if (!groupedByKey.has(c.groupKey)) groupedByKey.set(c.groupKey, []);
            groupedByKey.get(c.groupKey)!.push(c);
        }
    }

    const replaceDirectArray = (content: string, arrayName: string, newItems: string[]): { content: string; replaced: boolean } => {
        const findRegex = new RegExp(`export const ${escapeRegExp(arrayName)}\\s*=\\s*\\[([\\s\\S]*?)\\];`, 's');
        const match = findRegex.exec(content);
        if (!match) return { content, replaced: false };
        const replaceRegex = new RegExp(`(export const ${escapeRegExp(arrayName)}\\s*=\\s*)\\[[\\s\\S]*?\\](;)`, 's');
        const updated = content.replace(replaceRegex, `$1[${newItems.join(', ')}]$2`);
        return { content: updated, replaced: true };
    };

    const replaceObjectArray = (content: string, parent: string, arrayName: string, newItems: string[]): { content: string; replaced: boolean } => {
        const findRegex = new RegExp(`export const ${escapeRegExp(parent)}\\s*=\\s*{[\\s\\S]*?\\b${escapeRegExp(arrayName)}\\s*:\\s*\\[([\\s\\S]*?)\\](?=,|\\s*})`, 's');
        const match = findRegex.exec(content);
        if (!match) return { content, replaced: false };
        const replaceRegex = new RegExp(`(export const ${escapeRegExp(parent)}\\s*=\\s*{[\\s\\S]*?)(\\b${escapeRegExp(arrayName)}\\s*:\\s*)\\[[\\s\\S]*?\\](?=,|\\s*})`, 's');
        const updated = content.replace(replaceRegex, `$1$2[${newItems.join(', ')}]`);
        return { content: updated, replaced: true };
    };

    groupedByKey.forEach((items, groupKey) => {
        const sorted = [...items].sort((a, b) => (a.groupIndex! - b.groupIndex!));
        if (sorted.length === 0) return;
        const rendered = sorted.map(i => `${i.value}`);
        let result = replaceDirectArray(updatedContent, groupKey, rendered);
        if (!result.replaced) {
            for (const parent of ['BALANCE_SETTINGS', 'BALANCE_CONSTANTS']) {
                result = replaceObjectArray(updatedContent, parent, groupKey, rendered);
                if (result.replaced) break;
            }
        }
        updatedContent = result.content;
    });
    
    // Update the file content in the code array
    updatedCode[constantsFileIndex] = {
        ...updatedCode[constantsFileIndex],
        content: updatedContent
    };
    
    // console.log('Applied constants to code', updatedCode[constantsFileIndex]);
    return updatedCode;
};

// Helpers shared with visual: quoting and tokenization
const stripQuotes = (value: string): string => {
    if (
        (value.startsWith("'") && value.endsWith("'")) ||
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith('`') && value.endsWith('`'))
    ) {
        return value.slice(1, -1);
    }
    return value;
};

const stripInlineCommentsOutsideQuotes = (value: string): string => {
    let result = '';
    let inSingle = false;
    let inDouble = false;
    let inBacktick = false;
    let inBlock = false;
    for (let i = 0; i < value.length; i++) {
        const ch = value[i];
        const next = i + 1 < value.length ? value[i + 1] : '';
        if (inBlock) {
            if (ch === '*' && next === '/') {
                inBlock = false;
                i++;
                continue;
            }
            continue;
        }
        if (!inSingle && !inDouble && !inBacktick) {
            if (ch === '/' && next === '*') {
                inBlock = true;
                i++;
                continue;
            }
            if (ch === '/' && next === '/') {
                break;
            }
        }
        if (ch === '\\' && (inSingle || inDouble || inBacktick)) {
            result += ch;
            if (i + 1 < value.length) {
                result += value[i + 1];
                i++;
            }
            continue;
        }
        if (ch === "'" && !inDouble && !inBacktick) {
            inSingle = !inSingle;
        } else if (ch === '"' && !inSingle && !inBacktick) {
            inDouble = !inDouble;
        } else if (ch === '`' && !inSingle && !inDouble) {
            inBacktick = !inBacktick;
        }
        result += ch;
    }
    return result.trimEnd();
};

// --- Helpers ---
const tokenizeArrayItems = (body: string): string[] => {
    const items: string[] = [];
    let current = '';
    let inSingle = false;
    let inDouble = false;
    let inBacktick = false;
    let escape = false;
    for (let i = 0; i < body.length; i++) {
        const ch = body[i];
        if (escape) {
            current += ch;
            escape = false;
            continue;
        }
        if (ch === '\\') {
            current += ch;
            escape = true;
            continue;
        }
        if (ch === "'" && !inDouble && !inBacktick) {
            inSingle = !inSingle;
            current += ch;
            continue;
        }
        if (ch === '"' && !inSingle && !inBacktick) {
            inDouble = !inDouble;
            current += ch;
            continue;
        }
        if (ch === '`' && !inSingle && !inDouble) {
            inBacktick = !inBacktick;
            current += ch;
            continue;
        }
        if (ch === ',' && !inSingle && !inDouble && !inBacktick) {
            const trimmed = current.trim();
            if (trimmed.length > 0) items.push(trimmed);
            current = '';
            continue;
        }
        current += ch;
    }
    const last = current.trim();
    if (last.length > 0) items.push(last);
    return items;
};

const isNumericLiteral = (value: string): boolean => {
    return /^-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?$/.test(value.trim());
};

const isBooleanLiteral = (value: string): boolean => {
    return /^(true|false)$/i.test(value.trim());
};

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');